/**
 * Signup conversion fire — single source of truth.
 *
 * The sign_up GA4 event + Google Ads website conversion must fire exactly
 * once per account. Historically this lived only in /welcome, which is reached
 * solely via a fragile `fab_signup_confirming` sessionStorage marker. When the
 * email-confirmation link opens in a different tab/app, or the session settles
 * outside the 60s fallback window, /welcome is skipped and the conversion
 * never fires (observed: ~70% of confirmed users had signup_tracked_at NULL).
 *
 * This helper is invoked from BOTH the guaranteed post-MFA gate
 * (PrivateRoute via useSignupConversion) and the legacy /welcome page. The
 * claim is a single atomic conditional UPDATE so it is once-only and race-safe
 * across pages, tabs, and reloads at the database level.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { trackSignup } from '@/lib/analytics';
import { logger } from '@/lib/logger';

export type SignupConversionResult = 'fired' | 'already' | 'error';

export async function ensureSignupConversionFired(
  client: SupabaseClient,
  userId: string,
  email?: string,
): Promise<SignupConversionResult> {
  try {
    // Generated types don't include signup_tracked_at; cast to reach it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = client as any;

    // Atomic claim: one conditional UPDATE. Only the caller that flips
    // signup_tracked_at NULL -> now() gets a row back and proceeds to fire.
    // No read-then-write race; concurrent callers (Welcome + PrivateRoute +
    // other tabs) see zero rows and bail. We prefer a missed conversion over
    // a duplicate, since duplicates corrupt per-user bidding signals.
    const { data, error } = await db
      .from('profiles')
      .update({ signup_tracked_at: new Date().toISOString() })
      .eq('id', userId)
      .is('signup_tracked_at', null)
      .select('id');

    if (error) {
      logger.error('signupConversion: failed to claim signup_tracked_at', error);
      return 'error';
    }
    if (!data || data.length === 0) {
      return 'already';
    }

    await trackSignup(email);
    return 'fired';
  } catch (err) {
    logger.error('signupConversion: unexpected error', err);
    return 'error';
  }
}
